import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { ContextMenu } from '@/components/ui/ContextMenu';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import {
  useTradesList,
  useCounterpartyTabs,
  useBulkMark,
  useRepoMarginCallPreview,
  useRepoMarginCallPreviewSubset,
  usePostRepoMarginCall,
} from '@/hooks';
import {
  useMtmStore,
  useMtmFilterStore,
  useMtmColumnStore,
  useUIStore,
  useMtmDataStore,
  useFxRatesStore,
} from '@/stores';
import {
  TradeItem,
  transformPositionToTradeItem,
  OPEN_BOOK_STATUSES,
  isOpenBookStatus,
} from '@/types';
import type { BulkMark, BulkMarkFailure, BulkMarkRequest } from '@/types';
import { COB_FX_MAJORS, getCobRate } from '@/services/currency/cob-fx-service';
import { useCobEditableRate } from '@/features/trades/hooks/use-cob-editable-rate';
import {
  isMtmDefaultUnchecked,
  isMtmEligibleRow,
  isMtmMarkable,
  isRepoCallableInScope,
} from '@/utils/trades/mtm-cell-helpers';
import { applyLocalTradeFilters } from '@/utils/trades/apply-trade-filters';
import { buildMtmColumnDefs } from '@/utils/trades/build-mtm-columns';
import {
  getMtmSubmitPrice,
  computeMtmDialogDelta,
  evaluateMtaGate,
  formatCurrencyExact,
  resolveRepoCallSelection,
} from '@/utils/trades/mtm-calculations';
import { getTradeExposure } from '@/utils/trades/trade-math';
import { useCashPools } from '@/hooks/cash-pool';
import {
  counterpartyOptionLabel,
  isCounterpartyNotFound,
  useCounterpartyDetail,
} from '@/hooks/counterparty';
import { MtmAggregateBar } from '../components/mtm/MtmAggregateBar';
import { MtmCounterpartyTabs } from '../components/mtm/MtmCounterpartyTabs';
import { MtmSubmitBar } from '../components/mtm/MtmSubmitBar';
import { PoolSections, MtmPoolSection } from '../components/mtm/PoolSections';
import { useAuth } from '@/hooks/auth/use-auth';
import { MtmTable } from '../components/mtm/MtmTable';
import { MtmDetailsDrawer } from '../components/mtm/MtmDetailsDrawer';
import { CashPoolHistoryDrawer } from '../components/mtm/CashPoolHistoryDrawer';
import { PostRepoMarginCallDialog } from '../components/mtm/PostRepoMarginCallDialog';
import { createMtmColumns } from '../components/mtm/mtm-columns';
import { useMtmHeaderContextMenu } from '../hooks/useMtmHeaderContextMenu';
import {
  MTM_FILTERABLE_COLUMN_IDS,
  MTM_COLUMN_ID_TO_FILTER_ID,
} from '@/utils/trades/filter-column-mapping';
import { AppliedFiltersStrip } from '@/features/inventory/components/AppliedFiltersStrip';
// Direct import (not the '@/features/onboarding' barrel) so MtmPage's module
// graph doesn't pull in TourProvider/WhatsNewBanner and their store imports —
// that perturbs MtmPage.test's @/stores mock. TourSentinel only needs React.
import { TourSentinel } from '@/features/onboarding/components/TourSentinel';
import { getAppliedTradesFilterChips } from '@/utils/trades/filter-chip-labels';
import type { TradesFilterOptionSets } from '@/utils/trades/filter-chip-labels';
import { formatMta } from '@/utils/trades/mta-display';
import {
  TRADE_AGREEMENT_TYPE_OPTIONS,
  TRADE_STATUS_OPTIONS,
  TRADE_TERM_TYPE_OPTIONS,
  TRADE_TYPE_OPTIONS,
} from '@/features/trades/constants/filter-options';

// Stable empty exclusion set: includedPositions is already filtered to eligible &
// not-excluded rows, so computeMtmDialogDelta needs no further exclusion. A module-scope
// constant keeps the reference stable across renders (avoids needless memo invalidation).
const EMPTY_EXCLUDED: Set<number> = new Set<number>();

// MTM marks the OPEN book only (same OPEN_BOOK_STATUSES as the raw-status `rows` filter
// below, which reads it through isOpenBookStatus). Scoping
// the CP fetch to these statuses keeps cpPage.count at the MARKABLE book, not the CP's
// lifetime history — so isBookTruncated can't be permanently true for a large-history CP
// (kvi-r2 #3). `statuses` filters EFFECTIVE status, so the two derived buckets a raw-open
// row can map to are included (active→pending_partial_settlement,
// pending_open_settlement→partially_settled) or those rows would be dropped.
const MTM_QUERY_STATUSES = [
  ...OPEN_BOOK_STATUSES,
  'partially_settled',
  'pending_partial_settlement',
].join(',');

/**
 * Which half of the book this workspace marks (LR-587).
 *
 * Product answer #4 split the old Mark to Market tab in two and moved both under
 * Collateral Manager: `stock_lb` is the GMSLA MTM subtab, `repo` is the margin
 * call view that lives under Cash Pools -> GMRA, where the cash it moves is. One
 * component, because everything below the bucket - the counterparty tabs, the
 * grid, New Price editing, the filter sidebar - is the same workspace.
 */
export type MtmScope = 'stock_lb' | 'repo';

/**
 * Which GMSLA leg a stock_lb workspace shows (product spec 12.08): `fee` is the
 * Cash Pool Fee subtab, `rebate` the Cash Rebate one. A row's own
 * `rate_entry_mode` decides where it lives; rows that predate the mode default
 * to fee, the booking default. Repo has no legs — the prop is ignored there.
 */
export type MtmLeg = 'fee' | 'rebate';

const SCOPE_TRADE_TYPES: Record<MtmScope, readonly string[]> = {
  stock_lb: ['lend', 'borrow'],
  repo: ['repo', 'reverse_repo'],
};

// The bar renders its sections off the agreement, so pinning it to the scope's
// own agreement is what keeps each subtab to the one section it is about.
const SCOPE_AGREEMENT: Record<MtmScope, 'gmsla' | 'gmra'> = {
  stock_lb: 'gmsla',
  repo: 'gmra',
};

export const MtmPage: React.FC<{ scope?: MtmScope; leg?: MtmLeg }> = ({
  scope = 'stock_lb',
  leg,
}) => {
  const { cp: urlCp, product: urlProduct, leg: urlLeg } = useSearch({
    from: '/collateral',
  });
  const navigate = useNavigate();
  const resetMtm = useMtmStore((s) => s.reset);
  const localMtmFilters = useMtmFilterStore((s) => s.localMtmFilters);
  const setLocalMtmFilters = useMtmFilterStore((s) => s.setLocalMtmFilters);
  const clearLocalMtmFilters = useMtmFilterStore((s) => s.clearLocalMtmFilters);
  const resetLocalMtmFilter = useMtmFilterStore((s) => s.resetLocalFilter);

  // Phase 4: individual selectors to avoid re-render churn
  const priceOverrides = useMtmStore((s) => s.priceOverrides);
  const excludedIds = useMtmStore((s) => s.excludedIds);
  const setOverride = useMtmStore((s) => s.setOverride);
  const clearOverride = useMtmStore((s) => s.clearOverride);
  const toggleExclude = useMtmStore((s) => s.toggleExclude);
  const setExcluded = useMtmStore((s) => s.setExcluded);
  const excludeMany = useMtmStore((s) => s.excludeMany);
  const includeMany = useMtmStore((s) => s.includeMany);
  // LR-548: repo/reverse margin-call selection (deselected ids). Reset per CP via resetMtm.
  const repoExcludedIds = useMtmStore((s) => s.repoExcludedIds);
  const toggleRepoExclude = useMtmStore((s) => s.toggleRepoExclude);
  const setRepoExcluded = useMtmStore((s) => s.setRepoExcluded);

  // Tabs: the first page of the server's tab list (the active counterparties and the
  // deactivated ones that still have a book here, by name); the tab bar's menu
  // pages through the rest and searches the registry. Any other one, picked there or opened
  // by a ?cp= link, stands as a last tab, looked up by id when no loaded page holds it.
  const cpTabs = useCounterpartyTabs('collateral');
  const cpLoading = cpTabs.isLoading;
  const cpError = cpTabs.error;
  // Every loaded row, once the first page is in: the counterparties known without a lookup.
  const counterparties = cpTabs.data !== undefined ? cpTabs.rows : undefined;

  const [activeId, setActiveId] = useState<number | undefined>(undefined);
  // The counterparty asked for: the user's pick, else the link's. Is it among the tabs?
  const wantedId = activeId ?? urlCp;
  const wantedIsListed = (counterparties ?? []).some((c) => c.id === wantedId);
  // Not among them (deactivated, or a dead id): fetched on its own. Id 0 keeps it idle.
  const lookupId =
    counterparties !== undefined && wantedId !== undefined && !wantedIsListed ? wantedId : 0;
  const lookup = useCounterpartyDetail(lookupId);
  // In flight, or paused offline (where `isLoading` is false): not yet known to be dead. A
  // disabled lookup is pending too, hence the fetch status.
  const lookupPending = lookup.isPending && lookup.fetchStatus !== 'idle';
  // Only a 404 makes the id dead; any other failure keeps the link and shows the error. A
  // failed background refetch keeps the counterparty it already has.
  const lookupError =
    lookup.isError && lookup.data === undefined && !isCounterpartyNotFound(lookup.error)
      ? lookup.error
      : null;
  const firstPage = cpTabs.firstPage;
  const wantedRow =
    counterparties?.find((c) => c.id === wantedId) ??
    (lookup.data !== undefined && lookup.data.id === wantedId ? lookup.data : undefined);
  const extraCp =
    wantedRow && !(firstPage ?? []).some((c) => c.id === wantedRow.id) ? wantedRow : undefined;

  // The first page in server order, then the extra one; a deactivated one reads "(inactive)".
  const sortedCps = useMemo(() => {
    if (!counterparties) return [];
    const tabs = (firstPage ?? []).map((c) => ({
      id: c.id,
      name: counterpartyOptionLabel(c.name, c.isActive),
    }));
    if (extraCp) {
      tabs.push({ id: extraCp.id, name: counterpartyOptionLabel(extraCp.name, extraCp.isActive) });
    }
    return tabs;
  }, [counterparties, firstPage, extraCp]);
  // The rest of the tab list for the menu: loaded rows without a tab, and how many are not
  // loaded yet.
  const {
    total,
    rows: { length: loadedCount },
    hasNextPage: hasMoreTabs,
    fetchNextPage: fetchMoreTabs,
    isFetchingNextPage: isLoadingMoreTabs,
  } = cpTabs;
  const tabRest = useMemo(() => {
    const shown = new Set(sortedCps.map((c) => c.id));
    return {
      options: (counterparties ?? [])
        .filter((c) => !shown.has(c.id))
        .map((c) => ({ value: String(c.id), label: counterpartyOptionLabel(c.name, c.isActive) })),
      unloadedCount: Math.max(0, total - loadedCount),
      hasMore: hasMoreTabs,
      onLoadMore: () => void fetchMoreTabs(),
      isLoadingMore: isLoadingMoreTabs,
    };
  }, [sortedCps, counterparties, total, loadedCount, hasMoreTabs, fetchMoreTabs, isLoadingMoreTabs]);
  // LR-493: optional target currency to denominate the Run-MTM run in ('' = the GMSLA agreement's).
  const [targetCurrency, setTargetCurrency] = useState('');
  // LR-386: trade whose mark-history drawer is open (null = closed).
  const [detailsTrade, setDetailsTrade] = useState<TradeItem | null>(null);
  // True when an INCLUDED row in MtmTable has an invalid New Price. Reported by the table
  // (onValidityChange); blocks the Run-MTM submit so a row never marks at its default while
  // the field shows red. Self-resets: the table clears invalid state on counterparty switch.
  const [hasInvalid, setHasInvalid] = useState(false);
  // LR-548: true when a SELECTED repo row has an invalid New Price. Reported by MtmTable
  // (onRepoValidityChange); blocks the margin-call post so a bad price can't be sent.
  const [hasInvalidRepo, setHasInvalidRepo] = useState(false);

  // Resolve the active counterparty: the wanted one once it is known to exist (a tab, or
  // found by id), else the first tab.
  useEffect(() => {
    if (counterparties === undefined || lookupPending || lookupError) return;
    if (wantedId !== undefined && (wantedIsListed || extraCp)) {
      setActiveId(wantedId);
      return;
    }
    const resolved = sortedCps[0]?.id;
    setActiveId(resolved);
    // A well-formed but dead ?cp=<id> (a real id shape, no such counterparty) falls back
    // to the first tab; rewrite the URL so F5 / bookmark / shared link reproduce the
    // resolved cp instead of the dead id. Only rewrite when cp was supplied — leave a
    // clean /collateral (no cp) untouched. A MALFORMED one (`?cp=`, `?cp=true`) never
    // gets here: `parseCp` drops it to undefined rather than guessing an id, so it stays
    // in the link exactly as typed and the page opens on the default tab.
    if (urlCp !== undefined && urlCp !== resolved) {
      navigate({
        to: '/collateral',
        search: { product: urlProduct, leg: urlLeg, cp: resolved },
        replace: true,
      });
    }
  }, [
    counterparties,
    lookupPending,
    lookupError,
    wantedId,
    wantedIsListed,
    extraCp,
    sortedCps,
    urlCp,
    urlProduct,
    urlLeg,
    navigate,
  ]);

  // Clear price overrides + exclusions whenever the active counterparty changes
  // (reset-on-selection-key-change: the active counterparty is the selection key).
  // Tracks which matured ids have already had the default-exclude flush applied
  // for the current counterparty. Lets matured rows be auto-unchecked on first
  // load AND on any subsequent refetch that surfaces a NEW matured id (intraday
  // status transition active→matured via cron), while still honouring any
  // manual re-tick by the trader. Pre-r5 this was a boolean `done` flag — once
  // tripped, a later refetch's new matured rows arrived pre-checked, defeating
  // audit N4. LR-208 r5 M4.
  const defaultExcludeAppliedRef = useRef<{
    activeId?: number;
    seen: Set<number>;
  }>({
    seen: new Set(),
  });

  // Guard: skip only while activeId is still undefined (the pre-resolve renders). The
  // mtm-store is not persisted, so it is already empty at mount and there are no
  // overrides to discard — this guard is just to avoid a no-op reset before the first
  // counterparty resolves; it does NOT skip the undefined → first-value transition.
  useEffect(() => {
    if (activeId === undefined) return;
    resetMtm();
    setTargetCurrency('');  // LR-493: a target denomination is per-run; drop it on CP switch
    setFocusedPoolKey(null);  // LR-587: a pool focus is per-counterparty too
    defaultExcludeAppliedRef.current = { activeId, seen: new Set() };
  }, [activeId, resetMtm]);

  const handleTabChange = (id: number): void => {
    setActiveId(id);
    navigate({
      to: '/collateral',
      search: { product: urlProduct, leg: urlLeg, cp: id },
      replace: true,
    });
  };

  // LR-389: the active counterparty's minimum transfer amount (MTA) gates the displayed
  // margin-call transfer. Resolved from the full counterparty objects (sortedCps carries
  // only the id and the tab label). null = no threshold configured → gate inert.
  const activeCp = useMemo(
    () =>
      (counterparties ?? []).find((c) => c.id === activeId) ??
      (extraCp?.id === activeId ? extraCp : undefined),
    [counterparties, extraCp, activeId]
  );
  const activeName = activeCp?.name ?? '';
  const activeMta = activeCp?.minimumTransferAmount ?? null;
  const activeMtaCurrency = activeCp?.minimumTransferAmountCurrency;

  // MTM is scoped to one CP → fetch just its OPEN book (by FK id, comma-safe;
  // status-scoped, see MTM_QUERY_STATUSES) as one capped page instead of the whole
  // lifetime book + client filter.
  const {
    data: cpPage,
    isLoading,
    error,
  } = useTradesList(
    {
      counterparty_ids: activeId != null ? String(activeId) : undefined,
      statuses: MTM_QUERY_STATUSES,
      limit: 10000,
    },
    activeCp != null,
  );
  // A disabled query reports isLoading=false, so until the active counterparty resolves
  // the trades fetch hasn't started yet — that is loading, not an empty book.
  const tradesLoading =
    isLoading || cpLoading || lookupPending || (sortedCps.length > 0 && activeCp == null);
  // Without the counterparty list there is no book to show. A failed background refetch
  // keeps the cached list, so it doesn't replace the table.
  const tradesError = error ?? lookupError ?? (sortedCps.length === 0 ? cpError : null);
  const allTrades = useMemo<TradeItem[]>(
    () => (cpPage?.results ?? []).map(transformPositionToTradeItem),
    [cpPage],
  );
  // A CP whose OPEN book exceeds the cap would be silently truncated → block the run
  // (below) rather than mark an incomplete book. Scoped to markable statuses, so this
  // is now a genuine-overflow guard, not the CP's lifetime count.
  const isBookTruncated = !!cpPage && cpPage.count > cpPage.results.length;

  // Phase 4: Broaden the filter to include pending-settlement rows (they render gray/disabled).
  // "Open" = active || matured (eligible) + pending_open_settlement (ineligible) — read
  // from the shared set, not a literal, so it cannot drift from MTM_QUERY_STATUSES above.
  const rows = useMemo<TradeItem[]>(() => {
    // LR-587: and only the scope's own half of the book. Applied here, before
    // anything downstream, so the bar totals, the grid, the selection, the export
    // and the Run-MTM / margin-call scope all describe the same set - the subtab
    // the trader is looking at.
    const scopeTypes = SCOPE_TRADE_TYPES[scope];
    const filtered = (allTrades ?? []).filter(
      (t) =>
        t.counterparty === activeName &&
        scopeTypes.includes(t.trade_type) &&
        // 12.08 spec: the GMSLA workspace is split by leg (Cash Pool Fee /
        // Cash Rebate). An absent mode reads as REBATE - that is the model's own
        // default (`RateDetails.rate_entry_mode`), so it is what the backend
        // considers a row it never received a mode for.
        (leg === undefined || (t.rate_entry_mode ?? 'rebate') === leg) &&
        isOpenBookStatus(t.status)
    );
    // Dev-mode guard: warn if activeName is set, trades are loaded, but no rows matched.
    // This surfaces counterparty name mismatches between /counterparties and /positions
    // endpoints (e.g. trailing space, capitalisation, or out-of-sync rename).
    // `activeName &&` guards the transient initial state where activeName is '' (before
    // the first counterparty resolves). Without this guard the warning would fire on
    // every mount and produce spurious noise unrelated to a real mismatch. Do not remove it.
    if (
      import.meta.env.DEV &&
      activeName &&
      allTrades &&
      allTrades.length > 0 &&
      filtered.length === 0
    ) {
      console.warn(
        `[MtmPage] No ${scope} trades matched counterparty name:`,
        activeName,
        'Available names:',
        [...new Set(allTrades.map((t) => t.counterparty))]
      );
    }
    return filtered;
  }, [allTrades, activeName, scope, leg]);

  // LR-394: narrow rows by the MTM sidebar filters before anything downstream. The
  // result feeds the aggregate bar (so its bucket totals reflect the filters), the
  // table, and the Run-MTM scope. Counterparty is fixed by the active subtab, so the
  // sidebar omits that filter and applyLocalTradeFilters' counterparty branch is a no-op.
  const sidebarFilteredRows = useMemo<TradeItem[]>(
    () => applyLocalTradeFilters(rows, localMtmFilters),
    [rows, localMtmFilters]
  );

  // ─── LR-587: cash-pool sections (product answers #4/#5, 12.08) ──────────────
  // The pools of this workspace's product for the ACTIVE counterparty — a pool
  // no visible trade names yet is still a section (a freshly created pool, or
  // one whose book closed while still holding collateral). Scoped server-side to
  // the counterparty: filtering the whole desk's registry client-side made every
  // leg fetch it, and past the service's page cap a late-alphabet counterparty's
  // pools would have vanished from the strip with no signal.
  const { data: scopePools, isError: poolsUnavailable } = useCashPools(
    { product: SCOPE_AGREEMENT[scope], counterparty: activeId ?? undefined },
    { enabled: activeId != null },
  );
  const [focusedPoolKey, setFocusedPoolKey] = useState<string | null>(null);

  const poolSections = useMemo<MtmPoolSection[]>(() => {
    if (!activeName) return [];
    const byKey = new Map<string, MtmPoolSection>();
    // How many rows each pool has BEFORE the sidebar filters, so a section can say
    // whether its exposure describes the whole pool or only the visible slice.
    const unfilteredCounts = new Map<string, number>();
    for (const row of rows) {
      const key = row.cash_pool != null ? String(row.cash_pool) : 'none';
      unfilteredCounts.set(key, (unfilteredCounts.get(key) ?? 0) + 1);
    }
    for (const pool of scopePools ?? []) {
      if (pool.counterparty !== activeId) continue;
      // A switched-off pool is still shown while it holds money: deactivating is
      // how a pool is retired, and one retired with cash still in it is exactly
      // the state the desk needs to see rather than the one to hide.
      if (!pool.is_active && Number(pool.balance) === 0) continue;
      byKey.set(String(pool.id), {
        key: String(pool.id),
        name: pool.name,
        currency: pool.currency,
        rateDefinition: pool.rate_definition,
        balance: pool.balance,
        tradeCount: 0,
        exposureByCcy: new Map(),
        partiallyFiltered: false,
      });
    }
    for (const row of sidebarFilteredRows) {
      const key = row.cash_pool != null ? String(row.cash_pool) : 'none';
      let section = byKey.get(key);
      if (!section) {
        section = {
          key,
          name:
            key === 'none'
              ? 'No cash pool'
              : (row.cash_pool_name ?? `Pool ${key}`),
          // Deliberately NOT `row.currency`: that is the TRADE currency, while a
          // pool is in its agreement's currency, so a pool we could not read
          // stays unnamed rather than mislabelled.
          currency: null,
          rateDefinition: null,
          balance: null,
          tradeCount: 0,
          exposureByCcy: new Map(),
          partiallyFiltered: false,
        };
        byKey.set(key, section);
      }
      section.tradeCount += 1;
      // Kept PER trade currency: `getTradeExposure` is native to the security,
      // and the card converts each leg to the pool's currency at COB itself.
      const ccy = row.currency ?? '';
      section.exposureByCcy.set(
        ccy,
        (section.exposureByCcy.get(ccy) ?? 0) + getTradeExposure(row),
      );
    }
    // Answer #4 ordering: all USD pools first, then EUR, … — currency, then
    // name; the poolless remainder last, where it reads as the exception it is.
    const sections = [...byKey.values()].sort((a, b) => {
      if (a.key === 'none') return 1;
      if (b.key === 'none') return -1;
      return (
        (a.currency ?? '').localeCompare(b.currency ?? '') ||
        a.name.localeCompare(b.name)
      );
    });
    // The card's `balance` is the pool's WHOLE ledger balance (registry read), while its
    // exposure is summed over the rows the sidebar left visible. Their difference is the
    // indicative net call, so on a partly filtered pool it subtracts the whole balance from
    // part of the exposure and can print the opposite direction — "Due to us" on a book that
    // owes. Flag it and let the card drop the label rather than assert a direction it cannot
    // back (dp review, Medium 5). Filtering out rows of OTHER pools leaves this one exact.
    for (const section of sections) {
      section.partiallyFiltered =
        (unfilteredCounts.get(section.key) ?? 0) !== section.tradeCount;
    }
    // A lone "No cash pool" section separates nothing — one card that selects
    // everything is a control with no function, so hide the strip.
    if (sections.length === 1 && sections[0].key === 'none') return [];
    return sections;
  }, [scopePools, activeId, activeName, rows, sidebarFilteredRows]);

  // Focus narrows the workspace at the same point the sidebar filters do: the
  // bar, the grid, the selection, the export and the Run-MTM / margin-call
  // scope all describe the focused pool ("process amounts per cashpool").
  const poolScopedRows = useMemo<TradeItem[]>(() => {
    if (focusedPoolKey === null) return sidebarFilteredRows;
    return sidebarFilteredRows.filter((r) =>
      focusedPoolKey === 'none'
        ? r.cash_pool == null
        : String(r.cash_pool) === focusedPoolKey
    );
  }, [sidebarFilteredRows, focusedPoolKey]);

  // A focus whose section is gone (pool deactivated, rows filtered away, strip
  // hidden) would strand the trader on an empty grid with no in-strip way out —
  // auto-clear it.
  useEffect(() => {
    if (
      focusedPoolKey !== null &&
      !poolSections.some((s) => s.key === focusedPoolKey)
    ) {
      setFocusedPoolKey(null);
    }
  }, [focusedPoolKey, poolSections]);

  // LR-436 D6: the aggregate bar describes the MTM-ELIGIBLE book, not the whole
  // on-book set. Pending-settlement rows are not markable and are excluded from the
  // Run-MTM scope, so counting them in the bar's Value / Margin / Exposure
  // mis-states what a run will touch. Keep repo/reverse (isMtmEligibleRow, NOT
  // isMtmMarkable) so the Repo Value/Margin cells still aggregate. Default-unchecked
  // rows are dropped from the run internally by the bar's own excludedIds guard
  // (per-CP default-exclude), not here.
  const eligibleBarRows = useMemo<TradeItem[]>(
    () => poolScopedRows.filter((r) => isMtmEligibleRow(r)),
    [poolScopedRows],
  );

  // Currency-pickable basis for the aggregate bar's currency pill (LR-469): every
  // sidebar filter EXCEPT the pill's own settlement-ccy filter (LR-474 D3), so all
  // settlement currencies stay visible and pickable regardless of the current
  // selection. The sidebar's security-currency filter keeps narrowing the basis.
  // LR-436 r2: kept at ALL statuses (NOT narrowed to eligible). The pill is dual-
  // purpose — its selection also drives settlementCurrencies, which filters the
  // all-status TABLE below (which intentionally shows pending-settlement rows). So a
  // currency unique to pending-settlement trades MUST stay in the dropdown, or those
  // rows become unreachable as a table filter. If a trader picks such a currency, the
  // eligible-only bar correctly previews $0 for it (nothing markable) while the table
  // still lists the rows — an honest empty bar, not a bug.
  const currencyPickableRows = useMemo<TradeItem[]>(
    () =>
      applyLocalTradeFilters(rows, {
        ...localMtmFilters,
        settlementCurrencies: [],
      }),
    [rows, localMtmFilters]
  );

  // ─── LR-385: Repo margin call (per-counterparty cash pool) ──────────────────
  // Repo/reverse contracts are not repriced; the repo bucket shows a per-counterparty
  // margin call (Σ(current MV − booking value) netted against the cash pool). The pool
  // lives server-side, so the netted figures come from the backend preview endpoint. We
  // fetch only when the active counterparty actually has repo rows in view. The call is in the
  // GMRA agreement's currency; without one there is nothing to call.
  const gmraAgreement = activeCp?.agreements.find((agreement) => agreement.type === 'gmra');
  const noGmraAgreement = !gmraAgreement;
  const repoBaseCurrency = gmraAgreement?.currency ?? 'USD';
  const hasRepoRows = useMemo(
    () =>
      sidebarFilteredRows.some(
        (r) => r.trade_type === 'repo' || r.trade_type === 'reverse_repo'
      ),
    [sidebarFilteredRows]
  );
  // No GMRA agreement → no call currency, so no pool to read.
  const repoMarginCallQuery = useRepoMarginCallPreview(
    activeName || undefined,
    repoBaseCurrency,
    hasRepoRows && !noGmraAgreement
  );
  const postRepoMarginCall = usePostRepoMarginCall();
  const [showRepoMcConfirm, setShowRepoMcConfirm] = useState(false);
  // LR-479: the cash-pool posting-history drawer (opened from the bar's Cash pool cell).
  const [showCashPoolHistory, setShowCashPoolHistory] = useState(false);

  // ─── LR-548: repo margin-call subset selection + New Price overrides ─────────
  // Selectable universe = active|matured repo/reverse in the sidebar-filtered book (the rows that
  // carry checkboxes). DESELECTED ids live in repoExcludedIds (store, reset per CP). Overrides reuse
  // the shared priceOverrides map — repo ids are disjoint from stock and never enter the Run-MTM
  // payload (includedPositions filters on isMtmMarkable, which excludes repo).
  // Round-2 review #1: ONE predicate for every consumer of "is this row in the repo call" — the
  // selection, the book count and the CSV export basis (MtmTable holds the mirror for the checkboxes
  // and the footer). The export lagged a bucket-independence fix once already; a shared predicate is
  // what keeps the exported file reconciling with the on-screen totals.
  const isRepoRowCallable = useCallback(
    (r: TradeItem) =>
      isRepoCallableInScope(r, { noGmraAgreement }),
    [noGmraAgreement],
  );
  const repoSelectableRows = useMemo(
    () => poolScopedRows.filter(isRepoRowCallable),
    [poolScopedRows, isRepoRowCallable],
  );
  const repoTotalCount = repoSelectableRows.length;
  const repoSelectedIds = useMemo(
    () => repoSelectableRows.filter((r) => !repoExcludedIds.has(r.id)).map((r) => r.id),
    [repoSelectableRows, repoExcludedIds],
  );
  const repoSelectedCount = repoSelectedIds.length;
  // The whole callable repo book for this counterparty BEFORE the sidebar filters — what the
  // backend's omit-position_ids (whole-book) path would call. Same predicate as repoSelectableRows,
  // on unfiltered `rows`, so the two counts are directly comparable.
  const repoBookSelectableCount = useMemo(
    () => rows.filter(isRepoRowCallable).length,
    [rows, isRepoRowCallable],
  );
  // Whole-book vs strict-subset payload + the ≥1-selected post gate (LR-548). Omitting position_ids
  // is the tolerant whole-book path; it is used ONLY when the visible selection is provably the
  // entire book. See resolveRepoCallSelection for the rule (fixes silent hidden-row inclusion and
  // the empty-selection 400).
  const {
    positionIds: repoSelectionIds,
    canPost: repoCanPost,
    isSubset: repoIsSubsetCall,
  } = resolveRepoCallSelection(repoSelectedIds, repoTotalCount, repoBookSelectableCount);
  // New Price overrides for the SELECTED repo rows only — the wire array [{position_id, new_price}].
  // (The backend 400s on an override for an id not in the call.)
  const selectedRepoOverrides = useMemo(
    () =>
      repoSelectedIds
        .filter((id) => priceOverrides.has(id))
        .map((id) => ({ position_id: id, new_price: priceOverrides.get(id) as string })),
    [repoSelectedIds, priceOverrides],
  );

  // LR-479: `postedSigned` is the SIGNED amount the override dialog computed (sign fixed by the
  // computed net_due). Sent as a magnitude `posted_amount`; the backend re-applies the sign.
  // LR-479 review #2: also send the previewed direction — the backend rejects the post (400) if the
  // freshly-recomputed direction flipped since this preview, so the magnitude can't post a sign the
  // trader never saw.
  // LR-548: authoritative net-of-pool preview for the current selection + overrides. Fired only
  // while the Post dialog is open (enabled on showRepoMcConfirm). repoSelectionIds is undefined for
  // a true whole-book call (partial-skip-tolerant path) and the explicit id list otherwise.
  const repoSubsetPreviewQuery = useRepoMarginCallPreviewSubset(
    {
      counterpartyName: activeName || undefined,
      currency: repoBaseCurrency,
      positionIds: repoSelectionIds,
      priceOverrides: selectedRepoOverrides,
    },
    showRepoMcConfirm && hasRepoRows,
  );
  // The dialog reads the subset preview — the authoritative net-of-pool figures for the selection
  // (LR-548 acceptance 7). For a SUBSET call we must NOT fall back to the whole-book preview (its
  // amount/direction cover more positions than are being posted); the dialog shows a loading/empty
  // state until the subset preview settles instead. A true whole-book call may fall back safely
  // (identical computation).
  const repoDialogPreview =
    repoSubsetPreviewQuery.data ??
    (repoSelectionIds ? null : repoMarginCallQuery.data) ??
    null;

  // LR-493: `paymentCurrency` (null = pay in the settlement currency) is sent through so the backend
  // records the settlement→payment COB conversion. LR-548: also send the selection (position_ids for
  // a strict subset) + price_overrides, and take expected_direction from the subset preview so the
  // sign the trader confirmed is exactly the one posted.
  const handleConfirmRepoMarginCall = useCallback(
    (postedSigned: string, paymentCurrency: string | null, paymentFxRate: string | null) => {
      // review #6: `repoCanPost` is false once the selection is empty (nothing to call). A background
      // refetch that matured/removed every selected repo row while this dialog was open would otherwise
      // flip repoSelectionIds to undefined and silently post a WHOLE-BOOK call from a subset dialog.
      if (!activeName || !repoCanPost) return;
      postRepoMarginCall.mutate(
        {
          counterparty_name: activeName,
          currency: repoBaseCurrency,
          posted_amount: Math.abs(Number(postedSigned)).toFixed(2),
          expected_direction: repoDialogPreview?.direction,
          ...(repoSelectionIds ? { position_ids: repoSelectionIds } : {}),
          ...(selectedRepoOverrides.length ? { price_overrides: selectedRepoOverrides } : {}),
          ...(paymentCurrency ? { payment_currency: paymentCurrency } : {}),
          ...(paymentFxRate ? { payment_fx_rate: paymentFxRate } : {}),
        },
        { onSuccess: () => setShowRepoMcConfirm(false) }
      );
    },
    [
      activeName,
      repoBaseCurrency,
      postRepoMarginCall,
      repoDialogPreview,
      repoSelectionIds,
      repoCanPost,
      selectedRepoOverrides,
    ]
  );

  // Default-exclude the rows that arrive UNCHECKED (`isMtmDefaultUnchecked`), per
  // counterparty. Uses `excludeMany` (merge) NOT `setExcluded` (replace) so a manual
  // `toggleExclude` made in the brief window between `resetMtm` and the first non-empty
  // rows render survives the auto-exclude flush (audit feedback: replace wiped trader's
  // in-flight choices).
  //
  // LR-208 r5 M4: `seen` tracks the ids ALREADY auto-excluded this counterparty session,
  // so a refetch surfacing a NEW one (active→matured cron, or a leg settling mid-session)
  // is still auto-excluded while a row the trader deliberately re-ticked is not. Pre-r5 a
  // boolean `done` flag short-circuited every refetch after the first non-empty load.
  //
  // LR-608 r2: the pass is symmetric, because `partially_settled` is the first
  // default-unchecked status a row can LEAVE. `matured` is one-way (active→matured, always
  // INTO the policy), but a partially settled FOP graduates to `active` when its second leg
  // settles — and `seen` then held the auto-exclusion open forever, silently keeping a fully
  // settled active row out of the run, the footer, Current Exposure and the CSV with no cue
  // as to why. On graduation the id is released from both `seen` and `excludedIds`, so the
  // row rejoins the default-checked population it now belongs to. A hand-made exclusion of a
  // row that was partially settled at the time is lost by this — that click was
  // indistinguishable from the auto-state anyway, whereas the reverse (a graduated row stuck
  // out of the run) is both likelier and invisible.
  useEffect(() => {
    if (activeId === undefined) return;
    if (defaultExcludeAppliedRef.current.activeId !== activeId) return;
    if (rows.length === 0) return;
    const seen = defaultExcludeAppliedRef.current.seen;
    const newlyUncheckedIds: number[] = [];
    const graduatedIds: number[] = [];
    for (const r of rows) {
      if (isMtmDefaultUnchecked(r)) {
        if (!seen.has(r.id)) newlyUncheckedIds.push(r.id);
      } else if (seen.has(r.id)) {
        graduatedIds.push(r.id);
      }
    }
    if (newlyUncheckedIds.length > 0) {
      excludeMany(newlyUncheckedIds);
      for (const id of newlyUncheckedIds) seen.add(id);
    }
    if (graduatedIds.length > 0) {
      includeMany(graduatedIds);
      for (const id of graduatedIds) seen.delete(id);
    }
  }, [activeId, rows, excludeMany, includeMany]);

  // Phase 4 + 7: markable ids = MTM-eligible effective status AND NOT repo/reverse_repo.
  // Repo contracts are not repriced on this page (per "Repricing - MtM.xlsx" — handled by the
  // per-counterparty cash-pool margin call, LR-385), so they are excluded from the header
  // select-all and pre-selection. Ineligible (pending-settlement) ids are never in this set.
  // Scoped to poolScopedRows so a row hidden by the sidebar filters or the
  // focused pool is never markable.
  const eligibleIds = useMemo(
    () =>
      new Set(
        poolScopedRows.filter((r) => isMtmMarkable(r)).map((r) => r.id)
      ),
    [poolScopedRows]
  );

  // Publish the MTM book to the data store so the filter sidebar (a Layout sibling,
  // not a child) can drive the security-filter options and the CSV export (LR-469).
  // The export basis = the MARKING scope (stock markable rows) plus — LR-548 —
  // the selectable repo/reverse rows, all pre-exclusion. The footer applies the same predicate,
  // so the on-screen totals and the exported CSV reconcile (kvi #1); the export subtracts
  // excludedIds AND repoExcludedIds at click time, the footer live.
  // r2 review #1: the repo half is the CALLABLE set — `isRepoRowCallable`, shared with the selection
  // and the book count and mirrored by MtmTable for the checkboxes and the footer. A row with no
  // checkbox and no footer contribution (no GMRA agreement) must not
  // appear in the export either — that parity IS the reconciliation.
  const exportRows = useMemo(
    () => poolScopedRows.filter((r) => eligibleIds.has(r.id) || isRepoRowCallable(r)),
    [poolScopedRows, eligibleIds, isRepoRowCallable]
  );
  const setMtmData = useMtmDataStore((s) => s.setMtmData);
  useEffect(() => {
    setMtmData(rows, exportRows);
  }, [rows, exportRows, setMtmData]);
  // Clear the bridge when leaving the MTM page so a stale book can't linger in the
  // store (the security-filter options + CSV export read it). Separate unmount-only
  // effect — the publishing effect above must keep running on every data change.
  useEffect(() => () => setMtmData([], []), [setMtmData]);

  // Column manager state (LR-394): order, visibility and pins come from the MTM
  // column store. The synthetic price-block ids carry no TradeItem accessor — they
  // are rendered by MtmTable from per-row helpers — so they get a lightweight
  // placeholder def (id + header label) and are skipped by the createMtmColumns map.
  const mtmColumnConfig = useMtmColumnStore((s) => s.columns);
  const pinnedLeft = useMtmColumnStore((s) => s.pinnedLeft);
  const pinnedRight = useMtmColumnStore((s) => s.pinnedRight);
  const pinColumn = useMtmColumnStore((s) => s.pinColumn);
  const getPinState = useMtmColumnStore((s) => s.getPinState);
  const toggleColumnVisibility = useMtmColumnStore(
    (s) => s.toggleColumnVisibility
  );
  // LR-478: pass the active counterparty scope so trade-ID Links carry
  // from='mtm' + cp into the single-trade breadcrumb (returns to this scoped view).
  const allMtmColumns = useMemo(() => createMtmColumns({ cp: urlCp }), [urlCp]);
  const columns = useMemo(
    () => buildMtmColumnDefs(mtmColumnConfig, allMtmColumns),
    [mtmColumnConfig, allMtmColumns]
  );

  // ─── LR-394: header context menu + locate-from-filter-sidebar ──────────────
  const isColumnsTabActive = useUIStore((s) => s.columnsTabActive);
  const setShowFilterSidebar = useUIStore((s) => s.setShowFilterSidebar);
  const setFilterSidebarCollapsed = useUIStore(
    (s) => s.setFilterSidebarCollapsed
  );
  const setSidebarActiveTab = useUIStore((s) => s.setSidebarActiveTab);
  const setColumnsTabActive = useUIStore((s) => s.setColumnsTabActive);
  const setFocusedFilterId = useUIStore((s) => s.setFocusedFilterId);
  const expandFilter = useUIStore((s) => s.expandFilter);
  const locateColumnRequest = useUIStore((s) => s.locateColumnRequest);
  const setLocateColumnRequest = useUIStore((s) => s.setLocateColumnRequest);
  const locateHighlightedColumnIds = useUIStore(
    (s) => s.locateHighlightedColumnIds
  );
  const setLocateHighlightedColumnIds = useUIStore(
    (s) => s.setLocateHighlightedColumnIds
  );
  const highlightedColumnIds = locateHighlightedColumnIds ?? [];

  // The MTM page always wants the right filter/columns sidebar. Claim it on mount —
  // `showFilterSidebar` is a per-page-owned global flag (same pattern as InventoryPage /
  // CounterpartiesPage / CounterpartyReportsPage). Without this, reaching MTM by switching
  // from the Trades tab leaves the sidebar hidden: TradesPage's unmount cleanup sets the
  // flag false and the router swaps Trades↔MTM, so MtmPage must re-assert it (LR-438).
  // Reset on unmount (symmetric with TradesPage): every sidebar-owning page re-claims the
  // flag on its own mount, so leaving it true here would only leak onto consumers that read
  // it without a page gate — e.g. the Toast container positions off showFilterSidebar and
  // would right-align toasts on a no-sidebar page reached straight from MTM (LR-438).
  useEffect(() => {
    setShowFilterSidebar(true);
    return () => setShowFilterSidebar(false);
  }, [setShowFilterSidebar]);

  // "Filter by <col>" from the header menu: open the sidebar on the filters tab and
  // focus + expand the matching filter section. Counterparty has no MTM filter (the
  // active subtab fixes it), so it never appears as a filterable header here.
  const handleFilterColumnClick = useCallback(
    (columnId: string) => {
      setFilterSidebarCollapsed(false);
      setSidebarActiveTab('filters');
      setColumnsTabActive(false);
      const filterId = MTM_COLUMN_ID_TO_FILTER_ID[columnId] ?? columnId;
      setFocusedFilterId(filterId);
      expandFilter(filterId);
      setLocateHighlightedColumnIds(null);
    },
    [
      setFilterSidebarCollapsed,
      setSidebarActiveTab,
      setColumnsTabActive,
      setFocusedFilterId,
      expandFilter,
      setLocateHighlightedColumnIds,
    ]
  );

  const {
    headerCtxMenu,
    headerCtxMenuItems,
    handleColumnContextMenu,
    closeHeaderCtxMenu,
  } = useMtmHeaderContextMenu({
    columnConfig: mtmColumnConfig,
    getPinState,
    pinColumn,
    toggleColumnVisibility,
    handleFilterColumnClick,
    filterableColumnIds: MTM_FILTERABLE_COLUMN_IDS,
  });

  // Locate column (from the filter sidebar Locate button): scroll its header into view
  // and briefly highlight it. Mirrors TradesPage; MtmFilterSidebar already emits
  // setLocateColumnRequest, which was previously inert on the MTM page.
  useEffect(() => {
    if (!locateColumnRequest) return;
    const { columnId } = locateColumnRequest;
    setLocateHighlightedColumnIds([columnId]);
    const el = document.querySelector(`[data-column-id="${columnId}"]`);
    if (el) {
      el.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
    const t = setTimeout(() => {
      setLocateHighlightedColumnIds(null);
      setLocateColumnRequest(null);
    }, 2000);
    return () => clearTimeout(t);
  }, [
    locateColumnRequest,
    setLocateColumnRequest,
    setLocateHighlightedColumnIds,
  ]);

  // ─── LR-394: applied-filters strip (mirrors Trades, scoped to MTM filters) ──
  // Expanded state persisted independently from the Trades tab.
  const [appliedFiltersExpanded, _setMtmFiltersExpanded] = useState(
    () => localStorage.getItem('mtmFiltersExpanded') === 'true'
  );
  const setAppliedFiltersExpanded = useCallback((v: boolean) => {
    _setMtmFiltersExpanded(v);
    localStorage.setItem('mtmFiltersExpanded', String(v));
  }, []);

  // Counterparty is intentionally omitted — the active subtab fixes it, so that filter
  // is never set on MTM and never produces a chip.
  const mtmOptionSets = useMemo<TradesFilterOptionSets>(
    () => ({
      statuses: TRADE_STATUS_OPTIONS,
      tradeTypes: TRADE_TYPE_OPTIONS,
      agreementTypes: TRADE_AGREEMENT_TYPE_OPTIONS,
      termTypes: TRADE_TERM_TYPE_OPTIONS,
      // LR-474 r2 #5: no static `currencies` pool — see TradesPage (the
      // sidebar options are live-derived; "all"-collapse vs the mock pool lies).
    }),
    []
  );
  const appliedFilterChips = useMemo(
    () => getAppliedTradesFilterChips(localMtmFilters, mtmOptionSets),
    [localMtmFilters, mtmOptionSets]
  );
  // Settlement-ccy is surfaced by the aggregate-bar pill (with its own blue active
  // outline), not the applied-filters strip — so picking a currency no longer pops
  // the strip open. Exclude it from the strip's chips + count (mirrors TradesPage).
  const stripChips = useMemo(
    () =>
      appliedFilterChips.filter((c) => c.filterId !== 'settlementCurrencies'),
    [appliedFilterChips]
  );
  const handleAppliedChipClick = useCallback(
    (filterId: string) => {
      // LR-474 r2 #3: settlement-ccy filter lives in the aggregate-bar pill,
      // not the sidebar — don't open the sidebar to nothing.
      if (filterId === 'settlementCurrencies') return;
      setFilterSidebarCollapsed(false);
      setSidebarActiveTab('filters');
      setColumnsTabActive(false);
      setFocusedFilterId(filterId);
      expandFilter(filterId);
    },
    [
      setFilterSidebarCollapsed,
      setSidebarActiveTab,
      setColumnsTabActive,
      setFocusedFilterId,
      expandFilter,
    ]
  );
  const handleAppliedChipRemove = useCallback(
    (filterId: string) => resetLocalMtmFilter(filterId),
    [resetLocalMtmFilter]
  );

  // ─── BULK-04: submit flow (count → confirm → run) ──────────────────────────
  const [showConfirm, setShowConfirm] = useState(false);
  const bulkMark = useBulkMark();
  // LR-257: running MTM books marks (create_trades). Reuse MtmSubmitBar's
  // blockedReason to disable Run for users without the capability.
  const canRunMtm = useAuth().hasPermission('create_trades');

  // Included positions = the single source of truth for the count, the dialog
  // "After" total, and the marks[] payload: every eligible (active|matured) row that is
  // VISIBLE on the grid (`poolScopedRows`: sidebar filters + pool focus), minus excludedIds.
  // Scoping to the grid (not `rows`) makes the run match what the trader sees: a filter
  // that hides all rows gives a count of 0 and disables Run MTM, so hidden rows are never
  // marked. To keep overrides set on rows a filter hides, success clears only the SUBMITTED
  // ids' overrides (see handleConfirm), not the whole store.
  const includedPositions = useMemo(
    () =>
      poolScopedRows.filter((r) => isMtmMarkable(r) && !excludedIds.has(r.id)),
    [poolScopedRows, excludedIds]
  );
  const includedCount = includedPositions.length;

  // LR-493: the run can be denominated in a target currency only when the included positions share
  // ONE trade currency (a single COB rate can't denominate two — the backend also refuses a mixed
  // selection). Otherwise the selector is native-only. `effectiveTarget` is what we actually send:
  // blank when native-only, unset, or the target equals the trade currency (a no-op conversion).
  const includedCurrencies = useMemo(
    () => new Set(includedPositions.map((p) => (p.currency || '').toUpperCase())),
    [includedPositions]
  );
  const canTarget = includedCurrencies.size === 1;
  const nativeCcy = canTarget ? [...includedCurrencies][0] : null;
  // Without a target the backend denominates the run in the GMSLA agreement currency (in the trade
  // currency when there is no agreement), so that is the selector's default; picking the trade
  // currency sends it as the target, at rate 1.
  const gmslaCurrency =
    activeCp?.agreements.find((agreement) => agreement.type === 'gmsla')?.currency ?? null;
  const runDefaultCcy = gmslaCurrency ?? nativeCcy;
  const effectiveTarget =
    canTarget && targetCurrency && targetCurrency !== runDefaultCcy ? targetCurrency : '';
  const runConverts = Boolean(effectiveTarget && nativeCcy && effectiveTarget !== nativeCcy);

  // Dialog before→after margined-value totals, over included rows only.
  // Uses the same getMtmSubmitPrice rule as the marks[] payload (06-CONTEXT Area 3).
  // includedPositions is already eligible-and-not-excluded, so no further exclusion
  // is needed here — pass an empty set rather than re-filtering the same ids twice.
  const dialogDelta = useMemo(
    () =>
      computeMtmDialogDelta(includedPositions, priceOverrides, EMPTY_EXCLUDED),
    [includedPositions, priceOverrides]
  );

  // LR-493 (review CU1): the editable COB (T-1) FX rate the run is denominated at — default = COB
  // nativeCcy→target, overridable, re-seeds on a target change, never wipes a typed value, and fills
  // in when COB lands after mount. Shared verbatim with the repo "Pay in" rate
  // (PostRepoMarginCallDialog) via useCobEditableRate. `runRate` is the effective (possibly-edited)
  // rate; `runFxToSend` is the manual rate to send (null → the backend uses its own exact COB Decimal).
  const {
    rateStr: rateOverride,
    onRateChange: onRunRateChange,
    cobRate,
    cobRateStr,
    effectiveRate: runRate,
    rateToSend: runFxToSend,
  } = useCobEditableRate(nativeCcy ?? '', effectiveTarget, runConverts);
  // The default run converts too when the agreement is in another currency; preview it the same way.
  const defaultConverts = Boolean(
    !effectiveTarget && nativeCcy && gmslaCurrency && nativeCcy !== gmslaCurrency,
  );
  const fxVersion = useFxRatesStore((s) => s.version);
  const agreementCobRate = useMemo(
    () => (defaultConverts ? (getCobRate(nativeCcy ?? '', gmslaCurrency ?? '') ?? null) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fxVersion re-derives when COB lands
    [defaultConverts, nativeCcy, gmslaCurrency, fxVersion]
  );

  // LR-389: gate the signed net amount (dialogDelta.delta) by the active counterparty's MTA.
  // Soft gate — this only changes the displayed transfer amount/indicator, never blocks the run.
  // NOTE: this gates the signed net delta over included positions — a deliberately different
  // projection from the MetricsBar badge, which gates the aggregate Stock L/B net exposure
  // (MtmAggregateBar.tsx `stockLBGate`). When a run mixes in repo positions the two magnitudes
  // can diverge, so the dialog warning and the bar badge may not agree — this is expected.
  const dialogGate = useMemo(
    () => evaluateMtaGate(dialogDelta.delta, activeMta),
    [dialogDelta.delta, activeMta]
  );

  // Parse per-position failures from a 400 body. apiService rejects with the raw
  // AxiosError (see api-interceptors.ts), so the bulk_mark 400 body lives at
  // error.response.data.failed. The `data?.failed` fallback is defensive only.
  const failedPositions: BulkMarkFailure[] = useMemo(() => {
    const err = bulkMark.error as
      | (Error & {
          response?: { data?: { failed?: BulkMarkFailure[] } };
          data?: { failed?: BulkMarkFailure[] };
        })
      | null;
    return err?.response?.data?.failed ?? err?.data?.failed ?? [];
  }, [bulkMark.error]);

  // Run is also disabled in MtmSubmitBar; this guards the path defensively (e.g. programmatic
  // calls) so an empty or invalid-priced run can never open the confirm dialog.
  const handleRun = (): void => {
    if (includedCount === 0 || hasInvalid || isBookTruncated) return;
    bulkMark.reset();
    setShowConfirm(true);
  };

  const handleConfirm = (): void => {
    const submittedIds = includedPositions.map((p) => p.id);
    const marks: BulkMark[] = includedPositions.map((p) => ({
      position_id: p.id,
      price: getMtmSubmitPrice(p, priceOverrides.get(p.id)),
    }));
    // notes intentionally omitted in v1.1 (LOCKED discuss decision — see 06-CONTEXT
    // "Deferred Ideas": no notes field in v1.1). The endpoint and BulkMarkRequest.notes
    // both support it; this is a tracked deferral for a future phase, not an oversight.
    // LR-493: when a target currency is picked (and the selection is single-currency) send it so the
    // backend denominates the run in it; otherwise omit it and the run is in the agreement currency.
    const request: BulkMarkRequest = { marks };
    if (effectiveTarget) {
      request.target_currency = effectiveTarget;
      // Send the manual rate only when the trader edited it away from the COB default (or COB is
      // unavailable, so the typed rate is the only one). An untouched COB default is omitted so the
      // backend denominates at its own exact COB Decimal.
      if (runFxToSend != null) {
        request.fx_rate = runFxToSend;
      }
    }
    bulkMark.mutate(
      request,
      {
        onSuccess: () => {
          // Deliberate deviation from 06-CONTEXT Area 4's "reset() the store on success":
          // we clear ONLY the submitted rows' overrides — not the whole store — so a
          // filtered run never discards overrides the user set on rows the filter hides.
          // excludedIds are intentionally NOT reset here either: an exclusion the trader
          // made (e.g. a pending-settlement row) persists across runs until they switch
          // counterparty, where the useEffect on activeId clears it. The hook handles the
          // toast and the trade + cash pool invalidation (refetch).
          submittedIds.forEach((id) => clearOverride(id));
          setTargetCurrency('');  // LR-493: denomination is per-run — reset after a successful run
          setShowConfirm(false);
        },
      }
    );
    // On error we keep the dialog open so failedPositions render (no close, no clear).
  };

  const handleClose = (): void => {
    if (!bulkMark.isPending) setShowConfirm(false);
  };

  return (
    // Fills its host panel (LR-587 mounts this inside Collateral Manager, which owns
    // the page height). The MtmSubmitBar is an in-flow flex child below the Card, so it
    // takes its own height inside this column — subtracting it left a dead white strip
    // the bar's height tall below the bar (the gap LR-368 UAT flagged).
    <div className="relative flex min-h-0 flex-1 flex-col">
      <TourSentinel name="mtm-overview" />
      <Card className="flex flex-1 flex-col overflow-hidden rounded-none border-none">
        {/* Only the applied filters live here; without any it would be a blank strip above the tabs. */}
        {stripChips.length > 0 && (
          <CardHeader className="flex flex-col gap-2 py-2">
            {/* Header row: Applied-filters trigger, right-aligned (the subtab chrome
                belongs to the Collateral Manager page hosting this workspace). */}
            <div className="flex flex-row items-center justify-end gap-4">
              <button
                type="button"
                onClick={() =>
                  setAppliedFiltersExpanded(!appliedFiltersExpanded)
                }
                className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 focus:outline-none"
                aria-expanded={appliedFiltersExpanded}
                aria-label="Applied filters"
              >
                Applied filters: {stripChips.length}
                {appliedFiltersExpanded ? (
                  <ChevronUpIcon
                    className="h-4 w-4 text-blue-600"
                    aria-hidden
                  />
                ) : (
                  <ChevronDownIcon
                    className="h-4 w-4 text-blue-600"
                    aria-hidden
                  />
                )}
              </button>
            </div>
            {/* Expanded chip list opens between the tabs header and the counterparty tabs.
                The settlement-ccy chip is excluded (its home is the aggregate-bar pill). */}
            <AppliedFiltersStrip
              count={stripChips.length}
              chips={stripChips}
              onChipClick={handleAppliedChipClick}
              onChipRemove={handleAppliedChipRemove}
              // Clear all still wipes ALL local filters incl. the ccy — "clear all"
              // means all — which also resets the pill's outline.
              onClearAll={clearLocalMtmFilters}
              hideTrigger
              expanded={appliedFiltersExpanded}
              onToggleExpanded={setAppliedFiltersExpanded}
            />
          </CardHeader>
        )}
        <MtmCounterpartyTabs
          counterparties={sortedCps}
          rest={tabRest}
          activeId={activeId}
          onTabChange={handleTabChange}
          isLoading={cpLoading}
        />
        <MtmAggregateBar
          rows={eligibleBarRows}
          presenceRows={poolScopedRows}
          priceOverrides={priceOverrides}
          excludedIds={excludedIds}
          mta={activeMta}
          mtaCurrency={activeMtaCurrency}
          // LR-587: the preview nets the WHOLE book's pools (the counterparty's, in any
          // currency), so while one pool is focused its Cash pool and
          // Net margin call cells describe a different set from the Current Exposure
          // beside them — exposure 1M, pool 1M, net call 2M on one row. Withheld
          // rather than shown stale; the post dialog computes the focused figure.
          repoMarginCall={focusedPoolKey === null ? (repoMarginCallQuery.data ?? null) : null}
          repoMarginCallLoading={repoMarginCallQuery.isLoading}
          // Named only for a loaded counterparty with repo rows: while the list loads there is no
          // agreement to read yet, and a book with no repo has nothing to call.
          noGmraAgreement={noGmraAgreement && activeCp != null && hasRepoRows}
          onRequestPostRepoMarginCall={
            // isBookTruncated mirrors the Run-MTM guard: with only part of the CP's book loaded the
            // selection counts describe a slice, so "N of M" (and the whole-book-vs-subset decision
            // built on it) can't be trusted — block posting rather than call on an unverifiable scope.
            canRunMtm &&
            repoMarginCallQuery.data &&
            !hasInvalidRepo &&
            repoCanPost &&
            !isBookTruncated
              ? () => setShowRepoMcConfirm(true)
              : undefined
          }
          isPostingRepoMarginCall={postRepoMarginCall.isPending}
          onOpenCashPoolHistory={
            repoMarginCallQuery.data
              ? () => setShowCashPoolHistory(true)
              : undefined
          }
          currencyPickableRows={currencyPickableRows}
          selectedCurrencies={localMtmFilters.settlementCurrencies}
          onCurrenciesChange={(settlementCurrencies) =>
            setLocalMtmFilters({ ...localMtmFilters, settlementCurrencies })
          }
          // LR-587: the SUBTAB decides the section, not the counterparty's agreement -
          // this workspace only ever holds one family's rows, and a CP on the other
          // agreement would otherwise render a section with nothing in it.
          agreementType={SCOPE_AGREEMENT[scope]}
        />
        {/* LR-587 (answers #4/#5, 12.08): the book separated per cash pool —
            focusing a card narrows everything below to that pool. */}
        <PoolSections
          sections={poolSections}
          focusedKey={focusedPoolKey}
          onFocus={setFocusedPoolKey}
          showNetCall={scope === 'repo'}
          poolsUnavailable={poolsUnavailable}
        />
        <CardContent className="relative flex flex-1 flex-col overflow-hidden border-t border-gray-200 p-0">
          <MtmTable
            data={poolScopedRows}
            columns={columns}
            pinnedLeft={pinnedLeft}
            pinnedRight={pinnedRight}
            isLoading={tradesLoading}
            error={(tradesError as Error | null) ?? null}
            activeCounterpartyName={activeName}
            priceOverrides={priceOverrides}
            excludedIds={excludedIds}
            eligibleIds={eligibleIds}
            onSetOverride={setOverride}
            onClearOverride={clearOverride}
            onToggleExclude={toggleExclude}
            onSetExcludedAll={setExcluded}
            onValidityChange={setHasInvalid}
            repoExcludedIds={repoExcludedIds}
            noGmraAgreement={noGmraAgreement}
            // The "invalid New Price in a selected repo row" gate is measured against what the page posts.
            repoCallSelectedIds={repoSelectedIds}
            onToggleRepoExclude={toggleRepoExclude}
            onSetRepoExcludedAll={setRepoExcluded}
            onRepoValidityChange={setHasInvalidRepo}
            isRepoScope={scope === 'repo'}
            isColumnsTabActive={isColumnsTabActive}
            onContextMenuColumn={handleColumnContextMenu}
            onPinColumn={pinColumn}
            highlightedColumnIds={highlightedColumnIds}
            onOpenDetails={setDetailsTrade}
          />
        </CardContent>
      </Card>

      {headerCtxMenu && (
        <ContextMenu
          items={headerCtxMenuItems}
          position={{ x: headerCtxMenu.x, y: headerCtxMenu.y }}
          onClose={closeHeaderCtxMenu}
        />
      )}

      {scope === 'stock_lb' && (
        <MtmSubmitBar
          includedCount={includedCount}
          isPending={bulkMark.isPending}
          onRun={handleRun}
          blockedReason={
            !canRunMtm
              ? 'You do not have permission to run MTM'
              : isBookTruncated
                ? `Only ${cpPage?.results.length} of ${cpPage?.count} positions loaded — marking is blocked to avoid an incomplete run`
                : hasInvalid
                  ? 'Fix invalid price(s) to run MTM'
                  : undefined
          }
        />
      )}

      <MtmDetailsDrawer
        trade={detailsTrade}
        onClose={() => setDetailsTrade(null)}
      />

      {/* LR-479: cash-pool posting history (variant A) — opened from the bar's Cash pool cell. */}
      <CashPoolHistoryDrawer
        counterpartyName={showCashPoolHistory ? activeName : null}
        currency={repoBaseCurrency}
        cp={urlCp}
        onClose={() => setShowCashPoolHistory(false)}
      />

      <ConfirmDialog
        show={showConfirm}
        onClose={handleClose}
        title={`Run MTM for ${activeName}?`}
        confirmLabel="Run MTM"
        confirmColor="blue"
        isLoading={bulkMark.isPending}
        onConfirm={handleConfirm}
        error={
          failedPositions.length > 0 ? null : (bulkMark.error as Error | null)
        }
        errorFallback="MTM run failed. Please try again."
      >
        <p className="text-sm text-gray-700">
          You&apos;re about to mark {includedCount}{' '}
          {includedCount === 1 ? 'position' : 'positions'} for {activeName}.
        </p>
        {failedPositions.length > 0 && (
          <div className="mt-3 space-y-1 rounded border border-red-200 bg-red-50 px-3 py-2">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-red-700">
              Failed positions — fix or exclude to retry
            </p>
            {failedPositions.map((f) => (
              <p key={f.position_id} className="font-mono text-xs text-red-600">
                #{f.position_id} — {f.reason}
              </p>
            ))}
          </div>
        )}
        <div className="mt-3 space-y-1 rounded border border-slate-200 bg-slate-50 px-4 py-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Total Margined Value
          </p>
          {/* Full-precision (formatCurrencyExact) — a financial confirmation must show the
              EXACT figure being committed, not the compact $X.XK/$X.XM form used in the bar. */}
          <div className="flex items-center justify-between">
            {/* Baseline = last marked value, or booking value if never marked (moving
                baseline) — NOT always the book price, so the delta is the incremental call. */}
            <span
              className="text-xs text-slate-500"
              title="Last marked value, or booking value if never marked"
            >
              Before (baseline):
            </span>
            <span className="font-mono text-sm font-semibold tabular-nums text-slate-900">
              {formatCurrencyExact(dialogDelta.before, nativeCcy || undefined)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-500">
              After (submit price):
            </span>
            <span className="font-mono text-sm font-semibold tabular-nums text-slate-900">
              {formatCurrencyExact(dialogDelta.after, nativeCcy || undefined)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-500">Delta:</span>
            <span
              className={`font-mono text-sm font-semibold tabular-nums ${
                dialogDelta.delta > 0
                  ? 'text-green-600'
                  : dialogDelta.delta < 0
                    ? 'text-red-600'
                    : 'text-slate-900'
              }`}
            >
              {/* Sign prefix applied here; formatCurrencyExact formats the magnitude only. */}
              {dialogDelta.delta > 0 ? '+' : dialogDelta.delta < 0 ? '-' : ''}
              {formatCurrencyExact(Math.abs(dialogDelta.delta), nativeCcy || undefined)}
            </span>
          </div>
        </div>
        {/* LR-493: optionally denominate this run in a target currency. Enabled only when the
            included positions share one trade currency (a single COB rate can't denominate two);
            the backend recomputes the authoritative figure, this is a COB (T-1) preview. */}
        <div className="mt-3 rounded border border-slate-200 bg-slate-50 px-4 py-3">
          <div className="flex items-center justify-between">
            <label
              htmlFor="mtm-run-target-ccy"
              className="text-xs font-semibold uppercase tracking-wide text-slate-500"
            >
              Run in
            </label>
            <select
              id="mtm-run-target-ccy"
              // LR-493 (FE review #6): bind to effectiveTarget (not raw targetCurrency) so the value
              // always matches a rendered <option> — a stale pick equal to the trade ccy (its option
              // is filtered out) or a mixed selection resolves to '' here, never a ghost selection.
              value={effectiveTarget}
              disabled={!canTarget}
              onChange={(e) => setTargetCurrency(e.target.value)}
              className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-xs text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 disabled:opacity-50"
            >
              <option value="">
                {gmslaCurrency
                  ? `${gmslaCurrency} (agreement currency)`
                  : nativeCcy
                    ? `${nativeCcy} (trade currency)`
                    : 'Trade currency'}
              </option>
              {canTarget &&
                COB_FX_MAJORS.filter((c) => c !== runDefaultCcy).map((c) => (
                  <option key={c} value={c}>
                    {c === nativeCcy ? `${c} (trade currency)` : c}
                  </option>
                ))}
            </select>
          </div>
          {!canTarget && (
            <p className="mt-1.5 text-[11px] text-slate-400">
              Select positions in one trade currency to run in a different currency.
            </p>
          )}
          {defaultConverts && agreementCobRate != null && (
            <p className="mt-1.5 font-mono text-[11px] tabular-nums text-slate-500">
              Delta ≈ {dialogDelta.delta > 0 ? '+' : dialogDelta.delta < 0 ? '-' : ''}
              {formatCurrencyExact(Math.abs(dialogDelta.delta) * agreementCobRate, gmslaCurrency ?? undefined)} @ COB FX{' '}
              {Number(agreementCobRate.toFixed(6))}
            </p>
          )}
          {/* The preview rates cover COB_FX_MAJORS only; the backend resolves any pair, so a
              missing preview rate says nothing about what the run is recorded in. */}
          {defaultConverts && agreementCobRate == null && (
            <p className="mt-1.5 text-[11px] text-amber-700">
              No COB (T-1) preview for {nativeCcy}→{gmslaCurrency}. The run is recorded in{' '}
              {gmslaCurrency} at the server&apos;s COB rate, or in {nativeCcy} if it has none.
            </p>
          )}
          {runConverts && (
            <>
              {/* LR-493: editable FX rate — defaults to the COB (T-1) rate, but a trader can override
                  it or supply one when COB FX is unavailable for the pair. */}
              <div className="mt-2 flex items-center justify-between">
                <label htmlFor="mtm-run-fx-rate" className="text-[11px] text-slate-500">
                  FX rate {nativeCcy}→{effectiveTarget}
                </label>
                <input
                  id="mtm-run-fx-rate"
                  type="text"
                  inputMode="decimal"
                  value={rateOverride}
                  onChange={(e) => onRunRateChange(e.target.value)}
                  placeholder={cobRateStr || 'rate'}
                  className="w-28 rounded border border-slate-200 bg-white px-1.5 py-0.5 text-right font-mono text-xs tabular-nums text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500"
                />
              </div>
              {runRate != null ? (
                <p className="mt-1.5 font-mono text-[11px] tabular-nums text-slate-500">
                  Delta ≈ {dialogDelta.delta > 0 ? '+' : dialogDelta.delta < 0 ? '-' : ''}
                  {formatCurrencyExact(Math.abs(dialogDelta.delta) * runRate, effectiveTarget)} @ FX{' '}
                  {Number(runRate.toFixed(6))}
                  {cobRate == null && ' · manual (no COB)'}
                </p>
              ) : (
                <p className="mt-1.5 text-[11px] text-amber-700">
                  {cobRate == null
                    ? `No COB (T-1) preview for ${nativeCcy}→${effectiveTarget} — leave blank to try the backend COB rate, or enter one to override.`
                    : 'Enter a valid FX rate (greater than 0).'}
                </p>
              )}
            </>
          )}
        </div>
        {/* LR-389: MTA is a soft warning, NOT a hard gate. The MTM run always proceeds (marks
            post and move the baseline); when |net| is below the counterparty's minimum transfer
            amount we only surface a warning that no margin transfer is required this run. The
            actual exposure stays visible above as the Delta. */}
        {dialogGate.belowMta && (
          <div className="mt-3 flex items-start gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2">
            <ExclamationTriangleIcon
              className="mt-0.5 h-4 w-4 shrink-0 text-amber-600"
              aria-hidden
            />
            <p className="text-xs text-amber-800">
              Net exposure is{' '}
              <span className="font-semibold">
                below the minimum transfer amount
              </span>{' '}
              ({dialogGate.mta != null && formatMta(dialogGate.mta, activeMtaCurrency)}). MTM will
              still run — no margin
              transfer is required this run.
            </p>
          </div>
        )}
      </ConfirmDialog>

      {/* LR-385/LR-479: post the repo margin call with a manual override. Repo contracts are not
          repriced — the amount is prefilled with the computed call but editable to a rounded,
          counterparty-agreed figure; any residual rolls into the next call. */}
      <PostRepoMarginCallDialog
        show={showRepoMcConfirm}
        onClose={() => setShowRepoMcConfirm(false)}
        preview={repoDialogPreview}
        isLoading={postRepoMarginCall.isPending}
        previewLoading={repoSubsetPreviewQuery.isFetching}
        previewError={repoSubsetPreviewQuery.isError}
        error={postRepoMarginCall.error as Error | null}
        onConfirm={handleConfirmRepoMarginCall}
        selectedCount={repoSelectedCount}
        // review #2: the scope line and the subset ack are measured against the whole eligible BOOK,
        // with the visible count passed separately so a filtered view is called out explicitly.
        totalCount={repoBookSelectableCount}
        visibleCount={repoTotalCount}
        isSubsetCall={repoIsSubsetCall}
      />
    </div>
  );
};
